import { api } from './api';
import type { CardDetailData, AssignableUser } from '../pages/board-completo/card-detail/types';
import type { Sector } from './sectorService';
import type { BoardCategory } from './boardCategoryService';

// ─── Board ──────────────────────────────────────────────────────────────────

export interface ApiBoard {
  id: string;
  school?: string;
  title: string;
  description?: string;
  color?: string;
  sector_key: string;
  board_type: string;
  is_favorite: boolean;
  is_archived: boolean;
  last_activity_at?: string;
  total_cards?: number;
  overdue_cards?: number;
  completion_percentage?: number;
  // ── Setores/categorias customizáveis (M2M) ───────────────────
  sectors?: string[];
  sectors_detail?: Pick<Sector, 'id' | 'name' | 'slug' | 'color'>[];
  categories?: string[];
  categories_detail?: Pick<BoardCategory, 'id' | 'name' | 'slug' | 'color'>[];
  // Board hospedado noutra escola (ex: "Eleve Interno") mas sobre esta —
  // ex: board de Suporte por escola cliente. null na maioria dos boards.
  related_school_detail?: { id: string; school_name: string } | null;
  // Pode mexer na estrutura (nome, descrição, colunas, ordem, trava): gestão,
  // coordenação, quem criou o board e admin do board. Operador só trabalha
  // nos cards. Ausente em API antiga → tratar como true.
  can_manage_structure?: boolean;
}

export interface ApiList {
  id: string;
  title: string;
  position: number;
  status_mapping?: string;
  is_locked?: boolean;
  is_entry_point?: boolean;
  follow_up_enabled?: boolean;
  follow_up_after_days?: number;
  follow_up_entries?: FollowUpEntry[];
  cards?: ApiCard[];
  automation?: BoardListAutomation | null;
}

export interface MirrorCardResult {
  board_id: string;
  list_id?: string;
  success: boolean;
  error?: string;
  card?: ApiCard;
}

// ─── Follow-up ──────────────────────────────────────────────────────────────

export interface FollowUpEntry {
  id: number;
  board_list: string;
  day_offset: number;
  message_template: string;
  is_active: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface FollowUpEntryPayload {
  day_offset: number;
  message_template: string;
  is_active?: boolean;
  position?: number;
}

export interface ApiBoardLabel {
  id: number;
  name: string;
  color: string;
}

/** GET /boards/search-cards/ — um card achado pela busca da tela Setores. */
export interface CardSearchHit {
  id: string;
  title: string;
  is_archived: boolean;
  board_id: string;
  board_title: string;
  board_type: string;
  sector_key: string;
  list_title: string;
  updated_at: string | null;
}

export interface ApiCard {
  id: number;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  list: string;
  assigned_to_detail?: { id: number; username: string; full_name: string; avatar_url: string | null }[];
  due_date?: string | null;
  labels?: ApiBoardLabel[];
  tags?: string[];
  comment_count?: number;
  attachments_count?: number;
  is_overdue?: boolean;
  is_watching?: boolean;
  is_ai?: boolean;
  checklist_progress?: number | null;
  sla_applied?: boolean;
  last_completion_notification_status?: 'sent' | 'failed' | 'no_contact' | null;
  completion_requested_at?: string | null;
  completion_requested_by?: number | null;
  completion_request_seen_at?: string | null;
  // Data do pedido original e trava da descrição (apps/boards/services/card_origin.py).
  requested_at?: string | null;
  /** Quando o card entrou na coluna atual (BoardCard.list_entered_at). */
  list_entered_at?: string | null;
  description_locked?: boolean;
  created_at?: string;
  triage_status?: string;
  triage_origin?: string | null;
  // Card de triagem que originou este card (dispatch normal da triagem OU
  // escalada feita a partir do setor) — presente sempre que existe um
  // CardLink(link_type='triage_dispatch') com este card como alvo. Usado
  // pro badge/link "Ver na Triagem" no KanbanCard/CardHeader.
  triage_dispatch_origin?: { id: number; board_id: string; title: string } | null;
  channel?: string | null;
  created_by_name?: string | null;
  details?: Record<string, string>;
  is_parent?: boolean;
  parent_card?: number | null;
  step_order?: number | null;
  child_cards_count?: number;
  child_cards_completed_count?: number;
  sector_key?: string;
  request_summary?: {
    id?: number;
    request_type?: string;
    closure?: import('../pages/board-completo/card-detail/types').CardClosure | null;
    request_type_display?: string;
    siga_user_name?: string;
    requester_name?: string;
    requester_phone?: string;
    contact_name?: string;
    contact_phone?: string;
    candidate_name?: string;
    candidate_dob?: string;
    desired_grade?: string;
    desired_period?: string;
    protocol?: string;
    session_id?: number | null;
    // Presente só quando request_type === 'order' -- pedido da Loja.
    order_id?: number;
    order_number?: string;
    status?: string;
    status_display?: string;
    student_name?: string;
    total_amount?: string;
    payment_method?: string;
  } | null;
  updated_at?: string | null;
  archived_at?: string | null;
  is_archived?: boolean;
}

// Presente na resposta de moveCard() quando o card movido veio de um
// encaminhamento da triagem e entrou numa lista status_mapping='completed'
// — o card de triagem correspondente foi devolvido automaticamente para
// validação. Null quando o card não tem origem na triagem, ou quando não
// entrou numa lista 'completed'.
export interface TriageAutoReturn {
  triage_card_id: string;
  triage_card_title: string;
  triage_board_id: string;
}

export interface BoardCardSummary {
  id: number;
  title: string;
  triage_status?: string;
  step_order: number | null;
  list: {
    status_mapping: string;
    title: string;
  };
  board: {
    id: string;
    sector_key: string;
    title: string;
  };
}

export interface BoardStats {
  total_cards: number;
  overdue_cards: number;
  completion_percentage: number;
}

// ─── Comments ───────────────────────────────────────────────────────────────

export interface CardComment {
  id: number;
  content: string;
  author_detail: { id: number; username: string; full_name: string; avatar_url: string | null } | null;
  created_at: string;
  updated_at: string;
}

// ─── Attachments ────────────────────────────────────────────────────────────

export type DriveSyncStatus = 'synced' | 'pending' | 'error';

export interface CardAttachment {
  id: string;
  name: string;
  mime_type: string;
  size: number;
  uploaded_by_name: string | null;
  checklist_item?: number | null;
  created_at: string;
  download_url?: string;
  drive_file_id?: string;
  drive_web_view_link?: string;
  drive_synced_at?: string | null;
  drive_sync_error?: string;
  drive_sync_status?: DriveSyncStatus;
}

export interface DriveSyncResult {
  configured: boolean;
  synced: number;
  failed: number;
  skipped_no_enrollment?: boolean;
  attachments: CardAttachment[];
}

// ─── Checklist ──────────────────────────────────────────────────────────────

export interface ChecklistItem {
  id: number;
  text: string;
  is_completed: boolean;
  position: number;
  completed_at: string | null;
  attachments?: CardAttachment[];
  /** "Conferido" — a escola olhou o documento (painel Conferir pedido). Independe de recebido. */
  is_verified?: boolean;
  verified_at?: string | null;
  verified_by_name?: string | null;
}

export interface RefusalOptions {
  motivos: { value: string; label: string }[];
  mensagem_recusa: string;
  mensagem_lista_espera: string;
}

export interface CardChecklist {
  id: number;
  title: string;
  position: number;
  items: ChecklistItem[];
  progress: { completed: number; total: number; percentage: number };
}

// ─── Activity ───────────────────────────────────────────────────────────────

export interface BoardActivity {
  id: number;
  board: string;
  user: number | null;
  user_detail: { id: number; username: string; full_name: string; avatar_url: string | null } | null;
  card: number | null;
  card_title?: string | null;
  board_title?: string | null;
  sector_key?: string | null;
  action: string;
  action_display: string;
  description: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

// ─── Board Members ───────────────────────────────────────────────────────────

/** Filtros do board guardados numa visão salva (mesmas chaves do BoardCompleto). */
export interface SavedViewFilters {
  assignedToMe?: boolean;
  overdue?: boolean;
  priorities?: ('low' | 'medium' | 'high' | 'urgent')[];
  memberId?: string | null;
  q?: string;
  viewMode?: 'board' | 'list';
  listSort?: 'due' | 'requested' | 'priority';
  sortByDueDate?: 'none' | 'asc' | 'desc';
}

export interface ApiSavedView {
  id: number;
  name: string;
  filters: SavedViewFilters;
  position: number;
}

export interface BoardMember {
  id: string;
  name: string;
  initials: string;
  role: 'admin' | 'editor' | 'viewer';
}

// ─── Board List Automation ───────────────────────────────────────────────────

export interface BoardListAutomation {
  id: number;
  board_list: string;
  is_active: boolean;
  message_template: string;
  /** @deprecated manter por retrocompatibilidade com serializer */
  parent_doc_name: string | null;
  attached_files: {
    id: string;
    name: string;
    category: string;
    file_type: string;
  }[];
  delay_minutes: number;
  require_confirmation: boolean;
  recipient: 'primary' | 'all';
  duplicate_window_hours: number;
  created_at: string;
  updated_at: string;
}

export interface BoardListAutomationPayload {
  is_active?: boolean;
  message_template?: string;
  /** @deprecated use attachAutomationFiles instead */
  parent_doc_name?: string | null;
  delay_minutes?: number;
  require_confirmation?: boolean;
  recipient?: 'primary' | 'all';
  duplicate_window_hours?: number;
}

export interface DuplicateCheckResult {
  is_duplicate: boolean;
  last_sent_at: string | null;
  hours_ago: number | null;
  contact_name?: string | null;
}

export interface AutomationInfo {
  automation_id: number;
  list_id: string;
  list_title: string;
  require_confirmation: boolean;
  delay_minutes: number;
  contact_phone: string;
  contact_name: string;
  message_preview: string;
  attached_files: { id: string; name: string }[];
}

export interface AutomationLogPayload {
  card_id: number;
  contact_phone: string;
  contact_name: string;
  message_sent: string;
  was_sent?: boolean;
}

export interface AutomationLogEntry {
  id: number;
  board_list: number;
  board_list_title: string;
  card: number;
  card_title: string;
  contact_phone: string;
  contact_name: string;
  message_sent: string;
  sent_by: number | null;
  sent_by_name: string | null;
  sent_at: string;
  was_sent: boolean;
}

export interface AutomationLogResponse {
  count: number;
  num_pages: number;
  results: AutomationLogEntry[];
}

// ─── Visão Geral ────────────────────────────────────────────────────────────

export interface VisaoGeralResponse {
  triage: {
    entrada_count: number;
    em_validacao_count: number;
    encaminhado_count: number;
  };
  sectors: Array<{
    sector_id: string | null;
    sector_key: string | null;
    sector_name: string;
    color: string | null;
    boards_count: number;
    total_cards: number;
    overdue_cards: number;
    parado_cards: number;
    completion_percentage: number;
    high_priority_cards: number;
    last_activity_at: string | null;
  }>;
  volume: {
    hoje: number;
    semana: number;
    mes: number;
    daily: Array<{ date: string; count: number }>;
  };
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export interface DashboardKPI {
  total_enrollments?: number;
  active_enrollments?: number;
  pending_documents?: number;
  agent_requests?: number;
  enrollment_change?: string;
  document_change?: string;
  agent_change?: string;
}

// ─── Triage KPIs ────────────────────────────────────────────────────────────

export interface TriageKPI {
  em_aberto_hoje: number;
  atrasados: number;
  aguardando_minha_validacao: number;
  concluidos_semana: number;
  triagem_snapshot: {
    entrada: number;
    encaminhados: number;
    em_validacao: number;
  };
  carga_por_setor: Array<{
    sector_key: string;
    sector_name: string;
    em_aberto: number;
  }>;
  fila_do_gestor: Array<{
    card_id: number;
    board_id: string;
    triage_board_id: string;
    title: string;
    tipo_acao: 'validar' | 'urgente' | 'atrasado' | 'encaminhado';
    prioridade: string;
    sector_name: string;
    motivo_devolucao: string | null;
    horas_desde_criacao: number;
    criado_em: string;
  }>;
}

// ─── Service ────────────────────────────────────────────────────────────────

// ── Minha mesa (GET /boards/my-desk/) ──────────────────────────────────────
export interface MyDeskCard {
  id: number;
  board_id: string;
  board_title: string;
  sector_key: string | null;
  list_title: string;
  title: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  due_date: string | null;
  created_at: string;
  protocol: string;
  session_id: number | null;
  contact_phone: string;
  contact_name: string;
  comment?: { author_name: string; content: string; created_at: string };
}

export interface MyDesk {
  counts: { mine: number; overdue: number; due_today: number; unassigned: number; my_conversations: number };
  has_sector: boolean;
  mine: MyDeskCard[];
  unassigned: MyDeskCard[];
  waiting_reply: MyDeskCard[];
}

export const boardsService = {
  getMyDesk: () => api.get<MyDesk>('/boards/my-desk/'),
  takeNextMyDesk: () => api.post<MyDeskCard>('/boards/my-desk/take-next/', {}),

  listBoards: (filters?: { sector?: string; sectorKey?: string; category?: string; archived?: boolean; schoolId?: string }) =>
    api
      .get<{ results: ApiBoard[] } | ApiBoard[]>('/boards/', {
        params: {
          ...(filters?.sector    ? { sector:     filters.sector }    : {}),
          ...(filters?.sectorKey ? { sector_key: filters.sectorKey } : {}),
          ...(filters?.category  ? { category:   filters.category }  : {}),
          ...(filters?.archived  ? { archived:   'true' }            : {}),
          ...(filters?.schoolId  ? { school_id:  filters.schoolId }  : {}),
        },
      })
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  getBoard: (id: string) => api.get<ApiBoard>(`/boards/${id}/`),

  getBoardStats: (id: string) =>
    api.get<BoardStats>(`/boards/${id}/stats/`),

  // withCards=false: só as colunas (o kanban e o card puxam os cards de
  // /cards/) — com cards aninhados um board de 200 cards pesava 300 KB.
  getBoardLists: (id: string, options?: { withCards?: boolean }) =>
    api
      .get<{ results: ApiList[] } | ApiList[]>(`/boards/${id}/lists/`, {
        params: options?.withCards === false ? { include_cards: 'false' } : undefined,
      })
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  getBoardCards: async (id: string) => {
    // page_size=250 (máx. da LargePagination do BoardCardViewSet) + páginas
    // restantes em PARALELO a partir do `count` — antes eram páginas de 25
    // buscadas uma após a outra seguindo `next` (board de 1.000 cards = 40
    // round-trips em série).
    type Page = { results: ApiCard[]; next: string | null; count?: number } | ApiCard[];
    const PAGE_SIZE = 250;

    const first = await api.get<Page>(`/boards/${id}/cards/`, {
      params: { page_size: String(PAGE_SIZE) },
    });
    if (Array.isArray(first)) return first;

    const firstResults = first.results ?? [];
    if (!first.next) return firstResults;

    const pageSize   = firstResults.length || PAGE_SIZE;
    const totalPages = Math.ceil((first.count ?? 0) / pageSize);
    const rest = await Promise.all(
      Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) =>
        api.get<{ results: ApiCard[] }>(`/boards/${id}/cards/`, {
          params: { page_size: String(PAGE_SIZE), page: String(i + 2) },
        }),
      ),
    );

    return firstResults.concat(...rest.map((page) => page.results ?? []));
  },

  getChildCards: (boardId: string, cardId: number) =>
    api
      .get<{ results: BoardCardSummary[] } | BoardCardSummary[]>(
        `/boards/${boardId}/cards/?parent_card=${cardId}`,
      )
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  createBoard: (data: {
    title: string;
    description?: string;
    color?: string;
    sector_ids?: string[];
    category_ids?: string[];
    // Mantidos por compatibilidade com chamadas existentes que ainda
    // usam o fluxo antigo de setor fixo (board_type/sector_key).
    sector_key?: string;
    board_type?: string;
  }) =>
    api.post<ApiBoard>('/boards/', data),

  // Edita título, descrição, cor, setores (M2M) e categorias (M2M).
  // board_type/sector_key permanecem fora deste fluxo.
  updateBoard: (id: string, data: {
    title?: string;
    description?: string;
    color?: string;
    sector_ids?: string[];
    category_ids?: string[];
  }) =>
    api.patch<ApiBoard>(`/boards/${id}/`, data),

  // Hard delete — backend exige board arquivado e sem cards ativos.
  deleteBoard: (id: string) => api.delete<void>(`/boards/${id}/`),

  createCard: (boardId: string, data: Partial<ApiCard> & { label_ids?: number[] }) =>
    api.post<ApiCard>(`/boards/${boardId}/cards/`, data),

  updateCard: (boardId: string, cardId: number, data: Partial<ApiCard> & { label_ids?: number[] }) =>
    api.patch<ApiCard>(`/boards/${boardId}/cards/${cardId}/`, data),

  // 🏷️ Etiquetas (BoardLabel) — reutilizáveis dentro do board, personalizáveis
  // (nome + 1 de 8 cores). Cards podem ter múltiplas (M2M via label_ids).
  listLabels: (boardId: string) =>
    api
      .get<{ results: ApiBoardLabel[] } | ApiBoardLabel[]>(`/boards/${boardId}/labels/`, {
        params: { page_size: '100' },
      })
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  // Visões salvas — pessoais, por board (BoardSavedView).
  listSavedViews: (boardId: string) =>
    api.get<ApiSavedView[]>(`/boards/${boardId}/views/`),

  createSavedView: (boardId: string, data: { name: string; filters: SavedViewFilters }) =>
    api.post<ApiSavedView>(`/boards/${boardId}/views/`, data),

  updateSavedView: (boardId: string, viewId: number, data: { name?: string; filters?: SavedViewFilters }) =>
    api.patch<ApiSavedView>(`/boards/${boardId}/views/${viewId}/`, data),

  deleteSavedView: (boardId: string, viewId: number) =>
    api.delete<void>(`/boards/${boardId}/views/${viewId}/`),

  createLabel: (boardId: string, data: { name: string; color: string }) =>
    api.post<ApiBoardLabel>(`/boards/${boardId}/labels/`, data),

  updateLabel: (boardId: string, labelId: number, data: { name?: string; color?: string }) =>
    api.patch<ApiBoardLabel>(`/boards/${boardId}/labels/${labelId}/`, data),

  deleteLabel: (boardId: string, labelId: number) =>
    api.delete<void>(`/boards/${boardId}/labels/${labelId}/`),

  moveCard: (boardId: string, cardId: string, data: {
    target_list_id: string;
    position: number;
  }) =>
    api.post<ApiCard & { triage_auto_return: TriageAutoReturn | null }>(
      `/boards/${boardId}/cards/${cardId}/move/`, data,
    ),

  /** "Registrar desistência" (board de Matrícula): cancela a matrícula, tira o
   *  card do board e marca a família como perdida na Captação quando não sobra filho. */
  registerDropout: (boardId: string, cardId: string, data: { motivo_perda: string; motivo_perda_detalhe?: string }) =>
    api.post<{ status: string; archived: boolean; leads_lost: number[] }>(
      `/boards/${boardId}/cards/${cardId}/desistencia/`, data,
    ),

  /** Motivos de recusa e texto padrão do aviso à família (painel Conferir pedido). */
  getRefusalOptions: (boardId: string, cardId: string) =>
    api.get<RefusalOptions>(`/boards/${boardId}/cards/${cardId}/recusar/`),

  /** "Recusar" a matrícula — ou, sem vaga, pôr na lista de espera. */
  refuseEnrollment: (boardId: string, cardId: string, data: {
    motivo: string; detalhe?: string; lista_espera?: boolean; avisar_familia?: boolean; mensagem?: string;
  }) =>
    api.post<{ status: string; archived: boolean; leads_lost: number[]; family_notified: boolean }>(
      `/boards/${boardId}/cards/${cardId}/recusar/`, data,
    ),

  requestCompletion: (boardId: string, cardId: string) =>
    api.post<{ notified_count: number; next_allowed_at: string }>(
      `/boards/${boardId}/cards/${cardId}/request-completion/`,
    ),

  setChecklistItemVerified: (
    boardId: string,
    cardId: string,
    checklistId: number,
    itemId: number,
    is_verified: boolean,
  ) =>
    api.patch<ChecklistItem>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/${itemId}/`,
      { is_verified },
    ),

  previewAutomation: (boardId: string, cardId: string, targetListId: string) =>
    api.post<{ automation_info: AutomationInfo | null }>(
      `/boards/${boardId}/cards/${cardId}/preview-automation/`,
      { target_list_id: targetListId },
    ),

  devolverCard: (boardId: string, cardId: string, data: {
    return_reason: 'completed' | 'wrong_sector' | 'missing_info' | 'rejected';
    notes?: string;
  }) =>
    api.post<void>(`/boards/${boardId}/cards/${cardId}/devolver/`, data),

  mirrorCard: (boardId: string, cardId: string, targets: { board_id: string; list_id: string }[]) =>
    api.post<{ results: MirrorCardResult[] }>(
      `/boards/${boardId}/cards/${cardId}/mirror/`,
      { targets },
    ),

  /** "Mover" do Encaminhar: o card sai deste board e entra na entrada do destino. */
  moveCardToBoard: (boardId: string, cardId: string, targetBoardId: string, note: string) =>
    api.post<ApiCard>(
      `/boards/${boardId}/cards/${cardId}/move-to-board/`,
      { target_board_id: targetBoardId, note },
    ),

  createCardFromTemplate: (data: {
    board_id: string;
    request_type: string;
    field_values?: Record<string, string>;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    due_date?: string;
    channel?: string;
    /** Aluno/responsável do cadastro — o template cria um pedido ligado à família. */
    student_id?: string;
    guardian_id?: string;
  }) =>
    api.post<{
      card: ApiCard;
      dispatched_to: string[];
      /** Boards que receberam o card (sempre inclui o de origem). */
      boards?: { id: string; title: string }[];
      /** Pedido criado (tipos que viram pedido); null nos demais. */
      request_id?: string | null;
      protocol?: string | null;
    }>(
      `/boards/cards/from-template/`,
      data,
    ),

  starBoard: (id: string) =>
    api.post<void>(`/boards/${id}/star/`),

  deleteCard: (boardId: string, cardId: number) =>
    api.delete<void>(`/boards/${boardId}/cards/${cardId}/`),

  archiveCard: (boardId: string, cardId: number) =>
    api.post<{ is_archived: boolean; message: string }>(
      `/boards/${boardId}/cards/${cardId}/archive/`,
    ),

  // archived=only → só arquivados, mais recentes primeiro, paginado no
  // servidor. Com a limpeza diária de concluídos (backend,
  // archive_completed_cards) o volume de arquivados cresce todo dia — antes
  // vinha archived=true (ativos + arquivados) com filtro no client, cortado
  // em 100 antes do filtro.
  listArchivedCards: (boardId: string) =>
    api
      .get<{ results: ApiCard[] } | ApiCard[]>(`/boards/${boardId}/cards/`, {
        params: { archived: 'only', page_size: '100' },
      })
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  // Uma página de arquivados com busca no servidor (título e descrição) —
  // a gaveta de arquivados usa esta: filtrar só no client olhava apenas os
  // 100 mais recentes e não achava os antigos.
  listArchivedCardsPage: (boardId: string, opts: { search?: string; page?: number } = {}) =>
    api
      .get<{ results: ApiCard[]; next: string | null; count: number } | ApiCard[]>(`/boards/${boardId}/cards/`, {
        params: {
          archived: 'only',
          page_size: '50',
          page: String(opts.page ?? 1),
          ...(opts.search?.trim() ? { search: opts.search.trim() } : {}),
        },
      })
      .then((res) => (Array.isArray(res)
        ? { results: res, hasMore: false, count: res.length }
        : { results: res.results ?? [], hasMore: !!res.next, count: res.count ?? 0 })),

  // Só o total, pro badge do botão "Arquivados" — page_size=1 em vez de
  // baixar a lista inteira a cada abertura do board.
  countArchivedCards: (boardId: string) =>
    api
      .get<{ count: number } | ApiCard[]>(`/boards/${boardId}/cards/`, {
        params: { archived: 'only', page_size: '1' },
      })
      .then((res) => (Array.isArray(res) ? res.length : res.count ?? 0)),

  // Só os concluídos que a limpeza diária arquivou (archived=auto). Eles
  // continuam contando como conversão/efetivação — sem somar isso, as taxas
  // de Captação e Matrículas zeravam toda madrugada.
  countAutoArchivedCards: (boardId: string) =>
    api
      .get<{ count: number } | ApiCard[]>(`/boards/${boardId}/cards/`, {
        params: { archived: 'auto', page_size: '1' },
      })
      .then((res) => (Array.isArray(res) ? res.length : res.count ?? 0)),

  restoreCard:(boardId: string, cardId: number) =>
    api.post<{ is_archived: boolean; message: string }>(
      `/boards/${boardId}/cards/${cardId}/archive/`,
      undefined,
      { params: { archived: 'true' } },
    ),

  archiveBoard: (id: string) =>
    api.post<void>(`/boards/${id}/archive/`),

  // Retorna o card de triagem recém-criado (id + board), pra montar
  // imediatamente o link "Ver na Triagem" no card de origem sem esperar
  // um refetch — a API já devolve o BoardCardListSerializer completo do
  // card de triagem, aqui só tipado o subconjunto que o frontend usa.
  escalateToTriage: (boardId: string, cardId: number) =>
    api.post<{ id: number; board: string; title: string }>(
      `/boards/${boardId}/cards/${cardId}/escalar-triagem/`,
    ),

  getVisaoGeral: () =>
    api.get<VisaoGeralResponse>('/boards/overview/'),

  /** `includeArchived` busca também card arquivado (a API filtra ativos por padrão). */
  getCard: (boardId: string, cardId: string, includeArchived = false) =>
    api.get<CardDetailData>(
      `/boards/${boardId}/cards/${cardId}/`,
      includeArchived ? { params: { archived: 'true' } } : undefined,
    ),

  /** Cards de todos os boards que o usuário enxerga cujo título/descrição contém `q` (≥ 3 letras). */
  searchCards: (q: string) =>
    api.get<{ count: number; results: CardSearchHit[] }>('/boards/search-cards/', { params: { q } }),

  /**
   * Detalhe COMPLETO e somente-leitura de um card, resolvido só pelo pk
   * (sem precisar do board_id) — mirror-aware via GET /boards/cards/{id}/view/.
   * Usado pelo peek de card na Central de Atendimento (CardPeekDialog),
   * mesmo princípio de acesso do card compartilhado no chat interno.
   */
  getCardViewOnly: (cardId: number) =>
    api.get<CardDetailData>(`/boards/cards/${cardId}/view/`),

  getCardComments: (boardId: string, cardId: string) =>
    api
      .get<{ results: CardComment[] } | CardComment[]>(
        `/boards/${boardId}/cards/${cardId}/comments/`,
      )
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  createCardComment: (boardId: string, cardId: string, content: string) =>
    api.post<CardComment>(`/boards/${boardId}/cards/${cardId}/comments/`, { content }),

  updateCardComment: (boardId: string, cardId: string, commentId: number, content: string) =>
    api.patch<CardComment>(
      `/boards/${boardId}/cards/${cardId}/comments/${commentId}/`,
      { content },
    ),

  deleteCardComment: (boardId: string, cardId: string, commentId: number) =>
    api.delete<void>(`/boards/${boardId}/cards/${cardId}/comments/${commentId}/`),

  listAttachments: (boardId: string, cardId: string) =>
    api
      .get<{ results: CardAttachment[] } | CardAttachment[]>(
        `/boards/${boardId}/cards/${cardId}/attachments/`,
      )
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  uploadAttachment: (boardId: string, cardId: string, file: File, checklistItemId?: number) => {
    const formData = new FormData();
    formData.append('file', file);
    if (checklistItemId != null) {
      formData.append('checklist_item_id', String(checklistItemId));
    }
    return api.post<CardAttachment>(
      `/boards/${boardId}/cards/${cardId}/attachments/upload/`,
      formData,
    );
  },

  deleteAttachment: (boardId: string, cardId: string, attachmentId: string) =>
    api.delete<void>(
      `/boards/${boardId}/cards/${cardId}/attachments/${attachmentId}/`,
    ),

  getAttachmentDownloadUrl: (boardId: string, cardId: string, attachmentId: string) =>
    api.get<{ url: string; disposition: string }>(
      `/boards/${boardId}/cards/${cardId}/attachments/${attachmentId}/download-url/`,
    ),

  getDriveStatus: (boardId: string, cardId: string) =>
    api.get<{ drive_configured: boolean; is_enrollment_card: boolean; attachments: CardAttachment[] }>(
      `/boards/${boardId}/cards/${cardId}/attachments/drive-status/`,
    ),

  syncToDrive: (boardId: string, cardId: string) =>
    api.post<DriveSyncResult>(`/boards/${boardId}/cards/${cardId}/attachments/drive-sync/`),

  getDashboard: () =>
    api.get<DashboardKPI>('/secretary/dashboard/kpis/'),

  getCardChecklists: (boardId: string, cardId: string) =>
    api
      .get<{ results: CardChecklist[] } | CardChecklist[]>(
        `/boards/${boardId}/cards/${cardId}/checklists/`,
      )
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  createChecklist: (boardId: string, cardId: string, title: string) =>
    api.post<CardChecklist>(`/boards/${boardId}/cards/${cardId}/checklists/`, { title }),

  updateChecklist: (boardId: string, cardId: string, checklistId: number, title: string) =>
    api.patch<CardChecklist>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/`,
      { title },
    ),

  setChecklistPosition: (boardId: string, cardId: string, checklistId: number, position: number) =>
    api.patch<CardChecklist>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/`,
      { position },
    ),

  addChecklistItem: (boardId: string, cardId: string, checklistId: number, text: string) =>
    api.post<ChecklistItem>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/`,
      { text },
    ),

  deleteChecklistItem: (boardId: string, cardId: string, checklistId: number, itemId: number) =>
    api.delete<void>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/${itemId}/`,
    ),

  deleteChecklist: (boardId: string, cardId: string, checklistId: number) =>
    api.delete<void>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/`,
    ),

  toggleChecklistItem: (
    boardId: string,
    cardId: string,
    checklistId: number,
    itemId: number,
    is_completed: boolean,
  ) =>
    api.patch<ChecklistItem>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/${itemId}/`,
      { is_completed },
    ),

  updateChecklistItemText: (
    boardId: string,
    cardId: string,
    checklistId: number,
    itemId: number,
    text: string,
  ) =>
    api.patch<ChecklistItem>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/${itemId}/`,
      { text },
    ),

  setChecklistItemPosition: (
    boardId: string,
    cardId: string,
    checklistId: number,
    itemId: number,
    position: number,
  ) =>
    api.patch<ChecklistItem>(
      `/boards/${boardId}/cards/${cardId}/checklists/${checklistId}/items/${itemId}/`,
      { position },
    ),

  // archived=true: o histórico de um card arquivado (limpeza diária) também
  // aparece — só leitura; sem isso a rota devolvia 404 e a coluna ficava vazia
  // no drawer aberto pela tela Solicitações.
  getCardActivity: (boardId: string, cardId: string) =>
    api
      .get<{ results: BoardActivity[] } | BoardActivity[]>(
        `/boards/${boardId}/cards/${cardId}/activity/`,
        { params: { archived: 'true' } },
      )
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  getBoardMembers: (boardId: string) =>
    api
      .get<{
        owner: { user_detail: { id: number; username: string; full_name: string; avatar_url?: string | null }; role: string } | null;
        members: { id: number; user_detail: { id: number; username: string; full_name: string; avatar_url?: string | null }; role: string }[];
      }>(`/boards/${boardId}/members/`)
      .then((res) => {
        const toMember = (m: { user_detail: { id: number; full_name: string }; role: string }): BoardMember => ({
          id: String(m.user_detail.id),
          name: m.user_detail.full_name,
          initials: m.user_detail.full_name.slice(0, 2).toUpperCase(),
          role: (m.role as BoardMember['role']) ?? 'viewer',
        });
        const owner = res.owner ? [toMember(res.owner)] : [];
        return [...owner, ...(res.members ?? []).map(toMember)];
      }),

  assignCard: (boardId: string, cardId: string, userId: number | null) =>
    api.post<ApiCard>(`/boards/${boardId}/cards/${cardId}/assign/`, { user_id: userId }),

  watchCard: (boardId: string, cardId: string, watching: boolean) =>
    api.post<{ watching: boolean }>(`/boards/${boardId}/cards/${cardId}/watch/`, { watching }),

  // "Concluir e enviar" (Atendimento Autônomo, Etapa 4) — marca o
  // Request/SchoolDocument vinculado ao card como concluído e notifica o
  // responsável via WhatsApp (anexo real do card ou resposta livre do
  // operador). Ver CLAUDE.md do eleveia-api, seção
  // "atendimento-autonomo-etapa4-backend".
  completeAndNotify: (
    boardId: string,
    cardId: string,
    data: { mode: 'attachment' | 'response'; attachment_id?: string; message?: string },
  ) =>
    api.post<{ notified: boolean; protocol: string; contact_phone: string }>(
      `/boards/${boardId}/cards/${cardId}/complete-and-notify/`,
      data,
    ),

  // "Não concluir…" (ADR 0022): termina o pedido do card sem fazê-lo, com
  // motivo obrigatório; o card vai para a coluna "Não concluído".
  naoConcluir: (boardId: string, cardId: string, data: { reason: string; detail?: string }) =>
    api.post<{ card_id: string; list_id: string; not_completed_reason: string; not_completed_reason_label: string; detail: string }>(
      `/boards/${boardId}/cards/${cardId}/nao-concluir/`,
      data,
    ),

  getAssignableUsers: (boardId: string, cardId: string | number) =>
    api.get<AssignableUser[]>(`/boards/${boardId}/cards/${cardId}/assignable-users/`),

  getListAutomation: (boardId: string, listId: string) =>
    api.get<BoardListAutomation | null>(`/boards/${boardId}/lists/${listId}/automation/`),

  saveListAutomation: (boardId: string, listId: string, data: BoardListAutomationPayload) =>
    api.patch<BoardListAutomation>(`/boards/${boardId}/lists/${listId}/automation/`, data),

  attachAutomationFiles: (boardId: string, listId: string, fileIds: string[]) =>
    api.post<BoardListAutomation>(
      `/boards/${boardId}/lists/${listId}/automation/attach-files/`,
      { file_ids: fileIds },
    ),

  checkAutomationDuplicate: (
    boardId: string,
    listId: string,
    params: { card_id: number; contact_phone: string },
  ) =>
    api.get<DuplicateCheckResult>(
      `/boards/${boardId}/lists/${listId}/automation-check-duplicate/`,
      { params: { card_id: String(params.card_id), contact_phone: params.contact_phone } },
    ),

  logAutomationSend: (boardId: string, listId: string, data: AutomationLogPayload) =>
    api.post<void>(`/boards/${boardId}/lists/${listId}/automation-log/`, data),

  getCardAutomationLog: (boardId: string, cardId: number | string) =>
    api.get<AutomationLogEntry[]>(`/boards/${boardId}/cards/${cardId}/automation-log/`),

  getBoardAutomationLog: (boardId: string, params?: { list_id?: string; page?: number }) => {
    const qs = new URLSearchParams();
    if (params?.list_id) qs.set('list_id', params.list_id);
    if (params?.page)    qs.set('page', String(params.page));
    const query = qs.toString() ? `?${qs}` : '';
    return api.get<AutomationLogResponse>(`/boards/${boardId}/automation-log/${query}`);
  },

  updateList: (boardId: string, listId: string, data: Partial<{
    title: string;
    follow_up_enabled: boolean;
    follow_up_after_days: number;
  }>) =>
    api.patch<ApiList>(`/boards/${boardId}/lists/${listId}/`, data),

  createList: (boardId: string, data: { title: string }) =>
    api.post<ApiList>(`/boards/${boardId}/lists/`, data),

  deleteList: (boardId: string, listId: string) =>
    api.delete<void>(`/boards/${boardId}/lists/${listId}/`),

  reorderLists: (boardId: string, orderedIds: string[]) =>
    api.post<{ message: string }>(
      `/boards/${boardId}/lists/reorder/`,
      { ordered_ids: orderedIds },
    ),

  getFollowUpEntries: (boardId: string, listId: string) =>
    api
      .get<FollowUpEntry[] | { results: FollowUpEntry[] }>(
        `/boards/${boardId}/lists/${listId}/follow-up-entries/`,
      )
      .then((res) => (Array.isArray(res) ? res : res.results ?? [])),

  createFollowUpEntry: (boardId: string, listId: string, data: FollowUpEntryPayload) =>
    api.post<FollowUpEntry>(
      `/boards/${boardId}/lists/${listId}/follow-up-entries/`,
      data,
    ),

  updateFollowUpEntry: (
    boardId: string,
    listId: string,
    entryId: number,
    data: Partial<FollowUpEntryPayload>,
  ) =>
    api.patch<FollowUpEntry>(
      `/boards/${boardId}/lists/${listId}/follow-up-entries/${entryId}/`,
      data,
    ),

  deleteFollowUpEntry: (boardId: string, listId: string, entryId: number) =>
    api.delete<void>(
      `/boards/${boardId}/lists/${listId}/follow-up-entries/${entryId}/`,
    ),

  getTriageKpis: () =>
    api.get<TriageKPI>('/boards/triage-kpis/'),

  getPendingActionsCount: () =>
    api.get<{ count: number }>('/boards/pending-actions-count/'),

  getNewFromTriageCount: (boardId: string) =>
    api.get<{ count: number }>(`/boards/${boardId}/new-from-triage-count/`),
};